import React, { FC } from 'react';
import _ from 'lodash';
import { Box, Divider, Text, Checkbox } from '@wix/design-system';
import {
  Category,
  Service,
} from '@wix/ambassador-marketplace-categories-v1-category/types';
import { useTranslation } from '@wix/wix-i18n-config';
import { useExploreContext } from '@components/Contexts/ExploreContext';
import FilterPopover from '../../FilterPopover';

type ServiceWithSelection = Service & {
  isSelected: boolean;
};

type CategoryWithSelection = Category & {
  isSelected: boolean;
  servicePaths: Record<string, ServiceWithSelection>;
};

type Props = {
  isCategorySelected: (category: string) => boolean | undefined;
  hasSelectionData: boolean;
  selection: Record<string, CategoryWithSelection>;
  selectAService: (service: Service, category: string) => void;
  isServiceSelected: (
    service: Service,
    category: string,
  ) => boolean | undefined;
  selectCategory: (category: string) => void;
  onClear: () => void;
  onApply: () => void;
  numSelectedServices: number;
};

const Services: FC<Props> = ({
  isCategorySelected,
  hasSelectionData,
  selection,
  selectAService,
  isServiceSelected,
  selectCategory,
  onClear,
  onApply,
  numSelectedServices,
}) => {
  const { t } = useTranslation();
  const {
    state: { services },
  } = useExploreContext();

  const DividerWithSpace = () => {
    return (
      <Box paddingTop="18px" paddingBottom="18px">
        <Divider />
      </Box>
    );
  };

  const renderService = (service: Service, category: string) => {
    const { id, displayKey } = service;

    return (
      <div key={id}>
        <Box paddingTop="12px" paddingLeft="30px">
          <Checkbox
            dataHook={`${id}-checkbox`}
            id={id}
            checked={isServiceSelected(service, category)}
            onChange={() => {
              selectAService(service, category);
            }}
          >
            <Text size="small">{t(`explore.services.${t(displayKey!)}`)}</Text>
          </Checkbox>
        </Box>
      </div>
    );
  };

  const renderCategorySection = (
    category: string,
    servicesForCategory: Array<Service>,
  ) => {
    const isIndeterminate =
      !isCategorySelected(category) &&
      hasSelectionData &&
      _.size(_.filter(selection[category].servicePaths, (s) => s.isSelected!)) >
        0;

    return (
      <div key={category}>
        <Box direction="vertical">
          <Checkbox
            onChange={() => {
              selectCategory(category);
            }}
            checked={isCategorySelected(category)}
            indeterminate={isIndeterminate}
            dataHook={`${category}-subheader-checkbox`}
          >
            <Text size="small" dataHook={`${category}-subheader`}>
              {t(`explore.services.${category}`)}
            </Text>
          </Checkbox>
          {_.map(servicesForCategory, (service) => {
            return renderService(service, category);
          })}
        </Box>
        {category !== 'graphicdesign' && DividerWithSpace()}
      </div>
    );
  };

  return (
    <div data-hook="services-panel">
      <FilterPopover
        element={t('explore-filter-service')}
        panel="services"
        numSelectedServices={numSelectedServices}
        maxHeight="283px"
        minWidth={300}
        onClear={() => {
          onClear();
        }}
        onApply={() => {
          onApply();
        }}
      >
        <div data-hook="services-filter" style={{ maxWidth: 'fit-content' }}>
          {_.map(services, (category) => {
            const categoriesServices = category.services;
            return renderCategorySection(
              category.displayKey!,
              categoriesServices!,
            );
          })}
        </div>
      </FilterPopover>
    </div>
  );
};

export default Services;
